import React, { useState, useEffect } from 'react';
import { supabase } from '../../../utils/supabaseClient';
import { useAuth } from '../../../hooks/useAuth';
import { 
  Plus, 
  Trash2, 
  Search, 
  Mail, 
  AlertCircle, 
  CheckCircle,
  X,
  Send,
  Calendar,
  Clock,
  CalendarPlus
} from 'lucide-react';

/**
 * FeeWaiverList Component
 * 
 * Manages fee waivers for a specific submission within the Collection Detail admin view.
 * Adapted from CuratorFeeWaivers.jsx but scoped to a single submission.
 */
const FeeWaiverList = ({ submissionId, submissionTitle }) => {
  const { user } = useAuth();
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingWaiver, setCreatingWaiver] = useState(false);
  const [newWaiverEmail, setNewWaiverEmail] = useState('');
  const [newWaiverNotes, setNewWaiverNotes] = useState('');
  const [sendingEmail, setSendingEmail] = useState(null); // ID of waiver being emailed or null
  const [editingExpiryId, setEditingExpiryId] = useState(null);
  const [editingExpiryValue, setEditingExpiryValue] = useState('');
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [showExtendAll, setShowExtendAll] = useState(false);
  const [extendAllDate, setExtendAllDate] = useState('');
  const [extendingAll, setExtendingAll] = useState(false);
  const [newWaiverExpiry, setNewWaiverExpiry] = useState('');

  useEffect(() => {
    if (submissionId) {
      fetchWaivers();
    }
  }, [submissionId]);

  const fetchWaivers = async () => {
    try {
      setLoading(true);
      
      const { data: waiversData, error: waiversError } = await supabase
        .from('artist_submission_waivers')
        .select('*')
        .eq('submission_id', submissionId)
        .order('created_at', { ascending: false });

      if (waiversError) throw waiversError;

      // Enrich waivers with user data
      const enrichedWaivers = await Promise.all(
        (waiversData || []).map(async (waiver) => {
          const enrichedWaiver = { ...waiver };

          // Fetch user data
          if (waiver.user_id) {
            const { data: userData } = await supabase
              .from('users')
              .select('uuid, email, first_name, last_name')
              .eq('uuid', waiver.user_id)
              .single();
            enrichedWaiver.users = userData;
          }

          // Fetch invited_by user data
          if (waiver.invited_by) {
            const { data: invitedByUser } = await supabase
              .from('users')
              .select('uuid, email, first_name, last_name')
              .eq('uuid', waiver.invited_by)
              .single();
            enrichedWaiver.invited_by_users = invitedByUser;
          }

          return enrichedWaiver;
        })
      );

      setWaivers(enrichedWaivers);
    } catch (error) {
      console.error('Error fetching waivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWaiver = async (e) => {
    e.preventDefault();
    if (!newWaiverEmail) return;

    setCreatingWaiver(true);
    try {
      // 1. Find user by email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('uuid')
        .eq('email', newWaiverEmail)
        .single();

      if (userError || !userData) {
        throw new Error('User not found with that email address. They must have a VibrantCast account first.');
      }

      // 2. Check if waiver already exists
      const { data: existingWaiver } = await supabase
        .from('artist_submission_waivers')
        .select('id')
        .eq('user_id', userData.uuid)
        .eq('submission_id', submissionId)
        .single();

      if (existingWaiver) {
        throw new Error('Fee waiver already exists for this user.');
      }

      // 3. Create waiver
      const expiryDate = newWaiverExpiry 
        ? new Date(newWaiverExpiry + 'T23:59:59').toISOString()
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      const waiverData = {
        user_id: userData.uuid,
        submission_id: parseInt(submissionId),
        waiver_type: 'admin_override',
        invited_by: user.id,
        notes: newWaiverNotes,
        expires_at: expiryDate
      };

      const { error } = await supabase
        .from('artist_submission_waivers')
        .insert([waiverData]);

      if (error) throw error;

      // Note: Do NOT create artist_submission_mapping here.
      // The waiver record is sufficient to grant free access.
      // The mapping record will be created when the artist actually
      // submits through the SubmissionWizard, which ensures they
      // upload artwork first.

      // Reset and refresh
      setNewWaiverEmail('');
      setNewWaiverNotes('');
      setNewWaiverExpiry('');
      setShowCreateForm(false);
      fetchWaivers();
      alert('Fee waiver created successfully!');

    } catch (error) {
      console.error('Error creating waiver:', error);
      alert(error.message);
    } finally {
      setCreatingWaiver(false);
    }
  };

  const handleDeleteWaiver = async (waiverId) => {
    if (!confirm('Are you sure you want to delete this fee waiver? The artist will need to pay to submit.')) return;

    try {
      const { error } = await supabase
        .from('artist_submission_waivers')
        .delete()
        .eq('id', waiverId);

      if (error) throw error;
      
      fetchWaivers();
    } catch (error) {
      console.error('Error deleting waiver:', error);
      alert('Failed to delete fee waiver');
    }
  };

  const handleSendEmail = async (waiver) => {
    if (!waiver.users?.email) return;
    if (!confirm(`Send invitation email to ${waiver.users.email}?`)) return;

    setSendingEmail(waiver.id);
    try {
      // Fetch submission details for deadline info
      const { data: submissionDetails } = await supabase
        .from('submissions_details')
        .select('submission_deadline')
        .eq('submission_id', submissionId)
        .single();

      const emailData = {
        template_name: 'fee-waiver-invitation',
        subject: `Submit artwork now to ${submissionTitle}.`,
        message: {
          to: [{
            email: waiver.users.email,
            name: `${waiver.users.first_name || ''} ${waiver.users.last_name || ''}`.trim()
          }],
          global_merge_vars: [
            { name: 'ARTIST_FIRST_NAME', content: waiver.users.first_name || 'Artist' },
            { name: 'ARTIST_LAST_NAME', content: waiver.users.last_name || '' },
            { name: 'SUBMISSION_TITLE', content: submissionTitle },
            { name: 'WAIVER_TYPE', content: waiver.waiver_type?.replace('_', ' ') || 'Admin Override' },
            { name: 'EXPIRY_DATE', content: waiver.expires_at ? new Date(waiver.expires_at).toLocaleDateString() : 'No expiry' },
            { name: 'SUBMISSION_ID', content: String(submissionId) },
            { name: 'SUBMISSION_URL', content: `https://vibrantcast.com/artist/submissions/${submissionId}/apply` },
            { name: 'INVITED_BY_NAME', content: waiver.invited_by_users ? `${waiver.invited_by_users.first_name || ''} ${waiver.invited_by_users.last_name || ''}`.trim() : 'VibrantCast Team' },
            { name: 'SUBMISSION_DEADLINE', content: submissionDetails?.submission_deadline ? new Date(submissionDetails.submission_deadline).toLocaleDateString() : 'Contact us for details' },
            { name: 'NOTES', content: waiver.notes || '' }
          ]
        }
      };

      const { error } = await supabase.functions.invoke('send-mandrill-email', {
        body: emailData
      });

      if (error) throw error;
      alert('Email sent successfully!');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email.');
    } finally {
      setSendingEmail(null);
    }
  };

  const handleUpdateExpiry = async (waiverId) => {
    if (!editingExpiryValue) return;
    setSavingExpiry(true);
    try {
      const { error } = await supabase
        .from('artist_submission_waivers')
        .update({ expires_at: new Date(editingExpiryValue + 'T23:59:59').toISOString() })
        .eq('id', waiverId);

      if (error) throw error;
      setEditingExpiryId(null);
      setEditingExpiryValue('');
      fetchWaivers();
    } catch (error) {
      console.error('Error updating expiry:', error);
      alert('Failed to update expiry date');
    } finally {
      setSavingExpiry(false);
    }
  };

  const handleExtendAll = async () => {
    if (!extendAllDate) return;
    if (!confirm(`Extend all ${waivers.length} waiver(s) to ${new Date(extendAllDate).toLocaleDateString()}?`)) return;

    setExtendingAll(true);
    try {
      const { error } = await supabase
        .from('artist_submission_waivers')
        .update({ expires_at: new Date(extendAllDate + 'T23:59:59').toISOString() })
        .eq('submission_id', parseInt(submissionId));

      if (error) throw error;
      setShowExtendAll(false);
      setExtendAllDate('');
      fetchWaivers();
      alert('All waivers extended successfully!');
    } catch (error) {
      console.error('Error extending waivers:', error);
      alert('Failed to extend waivers');
    } finally {
      setExtendingAll(false);
    }
  };

  const startEditExpiry = (waiver) => {
    setEditingExpiryId(waiver.id);
    const d = waiver.expires_at ? new Date(waiver.expires_at) : new Date();
    setEditingExpiryValue(d.toISOString().split('T')[0]);
  };

  const getExpiryStatus = (expiresAt) => {
    if (!expiresAt) return { label: 'No expiry', color: '#6b7280' };
    const expiry = new Date(expiresAt);
    const now = new Date();
    if (expiry < now) return { label: 'Expired', color: '#dc2626' };
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 7) return { label: `${daysLeft}d left`, color: '#f59e0b' };
    return { label: `${daysLeft}d left`, color: '#16a34a' };
  };

  if (loading) return <div className="p-4 text-gray-500">Loading fee waivers...</div>;

  return (
    <div className="fee-waiver-list" style={{ marginTop: '24px', borderTop: '1px solid #e2e8f0', paddingTop: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 4px 0' }}>Fee Waivers</h3>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
            Grant free submission access to specific artists.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {waivers.length > 0 && (
            <button 
              onClick={() => setShowExtendAll(!showExtendAll)}
              className="btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', fontSize: '14px' }}
            >
              <CalendarPlus size={16} />
              Extend All
            </button>
          )}
          <button 
            onClick={() => setShowCreateForm(true)}
            className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', fontSize: '14px' }}
          >
            <Plus size={16} />
            Add Waiver
          </button>
        </div>
      </div>

      {showExtendAll && (
        <div style={{ 
          backgroundColor: '#fefce8', 
          padding: '16px', 
          borderRadius: '8px', 
          marginBottom: '16px',
          border: '1px solid #fde68a',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <CalendarPlus size={18} color="#d97706" />
          <span style={{ fontSize: '14px', fontWeight: '500' }}>Extend all waivers to:</span>
          <input
            type="date"
            value={extendAllDate}
            onChange={(e) => setExtendAllDate(e.target.value)}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontSize: '14px'
            }}
          />
          <button
            onClick={handleExtendAll}
            disabled={!extendAllDate || extendingAll}
            className="btn-primary"
            style={{ padding: '6px 16px', fontSize: '13px' }}
          >
            {extendingAll ? 'Extending...' : `Extend All (${waivers.length})`}
          </button>
          <button
            onClick={() => { setShowExtendAll(false); setExtendAllDate(''); }}
            style={{ padding: '6px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {showCreateForm && (
        <div style={{ 
          backgroundColor: '#f8fafc', 
          padding: '16px', 
          borderRadius: '8px', 
          marginBottom: '24px',
          border: '1px solid #e2e8f0'
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '15px' }}>Grant Fee Waiver</h4>
          <form onSubmit={handleCreateWaiver}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                Artist Email
              </label>
              <input
                type="email"
                value={newWaiverEmail}
                onChange={(e) => setNewWaiverEmail(e.target.value)}
                placeholder="artist@example.com"
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Artist must have a registered account.
              </p>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                Expiry Date
              </label>
              <input
                type="date"
                value={newWaiverExpiry}
                onChange={(e) => setNewWaiverExpiry(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Leave blank for 30-day default. This also serves as their personal deadline extension.
              </p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px' }}>
                Notes (Optional)
              </label>
              <textarea
                value={newWaiverNotes}
                onChange={(e) => setNewWaiverNotes(e.target.value)}
                placeholder="Reason for waiver..."
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="btn-secondary"
                style={{ padding: '8px 16px', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creatingWaiver}
                className="btn-primary"
                style={{ padding: '8px 16px', fontSize: '14px', minWidth: '100px' }}
              >
                {creatingWaiver ? 'Creating...' : 'Grant Waiver'}
              </button>
            </div>
          </form>
        </div>
      )}

      {waivers.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '32px', 
          backgroundColor: '#f9fafb', 
          borderRadius: '8px', 
          color: '#6b7280'
        }}>
          <p>No fee waivers granted yet.</p>
        </div>
      ) : (
        <div className="waivers-list">
          {waivers.map((waiver) => (
            <div key={waiver.id} style={{
              padding: '12px 16px',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              marginBottom: '8px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0', fontWeight: '600', color: '#1f2937' }}>
                    {waiver.users?.first_name} {waiver.users?.last_name}
                  </p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
                    {waiver.users?.email}
                  </p>
                  {waiver.notes && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                      "{waiver.notes}"
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleSendEmail(waiver)}
                    disabled={sendingEmail === waiver.id}
                    title="Send Invitation Email"
                    style={{
                      padding: '6px',
                      color: '#4f46e5',
                      backgroundColor: '#eef2ff',
                      borderRadius: '4px',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <Send size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteWaiver(waiver.id)}
                    title="Revoke Waiver"
                    style={{
                      padding: '6px',
                      color: '#dc2626',
                      backgroundColor: '#fef2f2',
                      borderRadius: '4px',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              {/* Expiry date row */}
              <div style={{ 
                marginTop: '8px', 
                paddingTop: '8px', 
                borderTop: '1px solid #f3f4f6',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px'
              }}>
                {editingExpiryId === waiver.id ? (
                  <>
                    <Calendar size={14} color="#6b7280" />
                    <span style={{ color: '#6b7280' }}>Expires:</span>
                    <input
                      type="date"
                      value={editingExpiryValue}
                      onChange={(e) => setEditingExpiryValue(e.target.value)}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        fontSize: '13px'
                      }}
                    />
                    <button
                      onClick={() => handleUpdateExpiry(waiver.id)}
                      disabled={savingExpiry}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      {savingExpiry ? '...' : 'Save'}
                    </button>
                    <button
                      onClick={() => { setEditingExpiryId(null); setEditingExpiryValue(''); }}
                      style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <Clock size={14} color={getExpiryStatus(waiver.expires_at).color} />
                    <span style={{ color: '#6b7280' }}>Expires:</span>
                    <span style={{ fontWeight: '500', color: getExpiryStatus(waiver.expires_at).color }}>
                      {waiver.expires_at ? new Date(waiver.expires_at).toLocaleDateString() : 'Never'}
                    </span>
                    <span style={{ 
                      fontSize: '11px', 
                      padding: '1px 6px', 
                      borderRadius: '9999px',
                      backgroundColor: getExpiryStatus(waiver.expires_at).color + '15',
                      color: getExpiryStatus(waiver.expires_at).color,
                      fontWeight: '600'
                    }}>
                      {getExpiryStatus(waiver.expires_at).label}
                    </span>
                    <button
                      onClick={() => startEditExpiry(waiver)}
                      title="Edit expiry date"
                      style={{
                        padding: '2px 8px',
                        fontSize: '12px',
                        color: '#6b7280',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeeWaiverList;
